import {
	Children,
	cloneElement,
	isValidElement,
	useEffect,
	useRef,
	useState,
} from "react";
import styled from "styled-components";
import css from "@styled-system/css";
import { variant } from "styled-system";

import Box from "../Box/Box";

import variants from "./variants";
import { ToggleOptionProps, ToggleProps, ToggleVariant } from "./toggle.type";
import ToggleOption from "./ToggleOption";

const ToggleContainer = styled(Box)`
	display: inline-flex;
	align-items: center;
	position: relative;
	border-radius: 40px;
	${css({
		padding: 1,
		backgroundColor: "toggle-bg-color",
	})}
`;

const ToggleSwitch = styled.span<{
	toggleSwitchLeft: number;
	variant: ToggleVariant;
	disabled: boolean;
}>`
	display: block;
	box-sizing: border-box;
	position: absolute;
	min-height: 35px;
	min-width: 35px;
	top: 4px;
	line-height: 1.7;
	border-radius: 40px;
	transition: all 300ms ease-in-out;
	cursor: default;
	white-space: nowrap;
	opacity: ${(props) =>
		props.variant === "default" ? "1" : props.disabled ? "0.3" : "1"};
	&[disabled] span {
		opacity: ${(props) => (props.variant === "default" ? "0.3" : "1")};
	}
	${(props) =>
		css({
			paddingX: 4,
			paddingY: 1,
			fontWeight: 2,
			left: props.toggleSwitchLeft ? `${props.toggleSwitchLeft}px` : "4px",
			boxShadow: "default",
		})};
	${variant({ variants })}
`;

const Toggle = ({
	id,
	children,
	disabled = false,
	activeIndex = null,
	onToggle = () => {},
}: ToggleProps) => {
	const [activeStateIndex, setActiveStateIndex] = useState<number | null>(null);
	const [toggleSwitchLeft, setToggleSwitchLeft] = useState<number>(0);

	const toggleButtonsRef = useRef<Array<HTMLButtonElement | null>>([]);

	const handleToggleSwitch = (index: number) => {
		setActiveStateIndex(index);
		onToggle(index);
	};

	useEffect(() => {
		if (
			activeIndex !== null &&
			activeIndex < Children.toArray(children).length
		) {
			setActiveStateIndex(activeIndex);
		}
	}, [activeIndex, setActiveStateIndex, children]);

	useEffect(() => {
		if (activeStateIndex !== null) {
			setToggleSwitchLeft(
				toggleButtonsRef.current[activeStateIndex]?.offsetLeft || 0
			);
		}
	}, [activeStateIndex]);

	const { children: activeItemLabel, variant } =
		activeStateIndex !== null && children
			? children[activeStateIndex].props
			: { children: [], variant: "default" };

	const childrenWithProps = Children.map(children, (child, index) => {
		return (
			isValidElement(child) &&
			cloneElement(child, {
				ref: (el: HTMLButtonElement) => (toggleButtonsRef.current[index] = el),
				className: activeStateIndex === index ? "active" : "",
				disabled,
				color:
					activeStateIndex === index && disabled && variant !== "default"
						? "primary-white"
						: "grey-blue",
				onClick: () => handleToggleSwitch(index),
			} as ToggleOptionProps)
		);
	});

	return (
		<ToggleContainer id={id}>
			{childrenWithProps}
			{activeIndex !== null && (
				<ToggleSwitch
					toggleSwitchLeft={toggleSwitchLeft}
					disabled={disabled}
					variant={variant || "default"}
					role="toggle"
				>
					<span>{activeItemLabel}</span>
				</ToggleSwitch>
			)}
		</ToggleContainer>
	);
};

Toggle.Option = ToggleOption;

export default Toggle;
